/**
 * デザイナー貢献度アンケートの型定義。
 *
 * 設問の選択肢は旧 Google Form と 1:1 で揃える。値そのものが集計シートへ転記され、
 * 既存の数式（高=3 / 中=2 / 低=1 への変換、貢献度の空セル=対象外 の件数集計）が
 * そのまま解釈するため、表記を変えてはいけない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイナー貢献度アンケートのタスク一覧・回答入力の型定義
 */

/** 評価カテゴリ。 @graph-connects none */
export type EvaluationCategory = 'デザイン' | '情報設計';

/** 解像度 5 軸の選択肢。 @graph-connects none */
export type ResolutionValue = '低' | '中' | '高';

/** 解像度の選択肢（表示順）。 @graph-connects none */
export const RESOLUTION_VALUES: readonly ResolutionValue[] = ['低', '中', '高'];

/** 貢献度 3 軸の選択肢。`対象外` は「デザイナーが貢献する余地がなかった」。 @graph-connects none */
export type ContributionValue = '対象外' | '1' | '2' | '3' | '4' | '5';

/** 貢献度の選択肢（表示順）。 @graph-connects none */
export const CONTRIBUTION_VALUES: readonly ContributionValue[] = [
  '対象外',
  '1',
  '2',
  '3',
  '4',
  '5',
];

/**
 * 解像度 5 軸の設問。並び順がそのままシートの J:N 列に対応する。
 * @graph-connects none
 */
export const RESOLUTION_AXES = [
  '【ラフ】プロジェクトの課題、ゴール設定',
  '【ラフ】それを解決するタッチポイントの設定',
  '【ラフ】具体Howの草案（ラフ）',
  '【設計】Howに基づいたラフ詳細',
  '【設計】Howに基づいた情報設計',
] as const;

/**
 * 貢献度 3 軸の設問。並び順がそのままシートの O:Q 列に対応する。
 *
 * シートへ書くのは**列位置で決まる回答値だけ**でこのラベル自体は転記しないので、
 * 表記は ID 看板の軸名（企画 / 設計 / デザイン実装）に合わせて読ませてよい。
 * 逆に**並びを変えると転記先の列がずれる**ので、並びは動かしてはいけない。
 * @graph-connects none
 */
export const CONTRIBUTION_AXES = ['企画（ラフ）', '設計', 'デザイン実装'] as const;

/** 解像度 5 軸の回答。 @graph-connects none */
export type ResolutionAnswer = [
  ResolutionValue,
  ResolutionValue,
  ResolutionValue,
  ResolutionValue,
  ResolutionValue,
];

/** 貢献度 3 軸の回答。 @graph-connects none */
export type ContributionAnswer = [ContributionValue, ContributionValue, ContributionValue];

/** アンケートタスク 1 件（子チケット 1 件 = アンケート 1 通）。 @graph-connects none */
export interface EvaluationTask {
  /** 子チケットキー。回答の一意キー */
  childIssueKey: string;
  /** 母票キー。担当案件テーブルの行と突き合わせる */
  parentIssueKey: string;
  /** 母票件名。母票が担当案件一覧から落ちていても見出しを描けるようにする */
  parentSummary: string;
  childSummary: string;
  /** 対象デザイナー */
  designer: string;
  category: EvaluationCategory;
  /** 該当Pt（actualHours のスナップショット） */
  pt: number;
  /** 対象月度 "YYYY/MM" */
  month: string;
  /** 完了日 "YYYY-MM-DD" */
  completedDate: string;
  state: 'pending' | 'answered';
  /**
   * 回答者がどの立場で対象になったか。
   * `survey_assignee` = 社外デザイナー子票で子票名末尾に指名された本人。
   */
  responderRole: 'pl' | 'main_worker' | 'survey_assignee';
  /** 回答済みのときだけ入る */
  respondedBy: string | null;
  respondedAt: string | null;
}

/** GET /idt-design-evaluation/my-tasks のレスポンス。 @graph-connects none */
export interface MyEvaluationTasksResponse {
  tasks: EvaluationTask[];
  /** 未回答件数。タブ行のバッジに出す */
  pendingCount: number;
}

/** POST /idt-design-evaluation/answers のリクエスト。 @graph-connects none */
export interface SubmitEvaluationAnswerInput {
  childIssueKey: string;
  resolution: ResolutionAnswer;
  contribution: ContributionAnswer;
  good: string | null;
  more: string | null;
}

/** POST /idt-design-evaluation/answers のレスポンス。 @graph-connects none */
export interface SubmitEvaluationAnswerResponse {
  childIssueKey: string;
  respondedAt: string;
  /** シート転記まで完了したか。false でも回答は保存済み */
  sheetSynced: boolean;
}
